import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';

interface AILiteracyDisclaimerProps {
  open: boolean;
  onClose: () => void;
}

const AILiteracyDisclaimer: React.FC<AILiteracyDisclaimerProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #eee'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#1976d2' }} />
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
            AI Literacy Note & Disclaimer
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ maxWidth: '100%' }}>
          <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
            <strong>Effective Date:</strong> {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>

          <Box sx={{ 
            bgcolor: '#fff3cd', 
            border: '1px solid #ffc107', 
            borderRadius: 1, 
            p: 2, 
            mb: 3,
            display: 'flex',
            gap: 1
          }}>
            <WarningIcon sx={{ color: '#ff9800', mt: 0.5 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#856404' }}>
                Important: AI-Generated Content
              </Typography>
              <Typography variant="body2" sx={{ color: '#856404', lineHeight: 1.6 }}>
                The responses provided by Gamura are generated using artificial intelligence. While we strive for accuracy, 
                AI-generated content may contain errors, inaccuracies, or outdated information. Always verify critical 
                information with official sources and consult qualified professionals for regulatory compliance matters.
              </Typography>
            </Box>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a', mt: 3 }}>
            1. Understanding AI Technology
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
            Gamura uses advanced AI language models to analyze documents and generate responses. This technology:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Processes information from uploaded documents and regulatory databases
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Generates responses based on patterns learned from training data
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • May not always distinguish between verified facts and general knowledge
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Can make mistakes, including factual errors, outdated information, or misinterpretations
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
            2. Limitations of AI Responses
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
            AI-generated responses have inherent limitations:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • <strong>Not Legal or Regulatory Advice:</strong> Responses are informational only and do not constitute legal, 
            regulatory, or professional advice. Always consult qualified professionals for compliance matters.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • <strong>Accuracy Not Guaranteed:</strong> While we use reliable sources, AI may generate incorrect, incomplete, 
            or outdated information. Verify all critical information independently.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • <strong>Context Limitations:</strong> AI may not fully understand complex regulatory contexts or jurisdiction-specific 
            nuances. Responses should be reviewed by subject matter experts.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • <strong>No Real-Time Updates:</strong> Information may not reflect the most recent regulatory changes or updates.
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • <strong>Potential Bias:</strong> AI systems may reflect biases present in training data or source materials.
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
            3. Best Practices for Using AI-Generated Content
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Verify information with official regulatory sources and documentation
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Consult qualified regulatory professionals for compliance decisions
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Cross-reference AI responses with multiple authoritative sources
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Review and validate all information before making regulatory or business decisions
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Use AI responses as a starting point for research, not as final answers
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Report any errors or concerns through the feedback system
          </Typography>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
            4. Regulatory Compliance Disclaimer
          </Typography>
          <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
            <strong>Important:</strong> Gamura is a tool designed to assist with regulatory research and documentation. 
            It is not a substitute for professional regulatory, legal, or compliance advice. Users are solely responsible for:
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Ensuring compliance with all applicable regulations and standards
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Verifying the accuracy and currency of all information used for compliance purposes
          </Typography>
          <Typography variant="body2" sx={{ mb: 1, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Consulting with qualified professionals before making regulatory submissions or compliance decisions
          </Typography>
          <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6, ml: 2 }}>
            • Any consequences resulting from reliance on AI-generated content
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Box sx={{ 
            bgcolor: '#e3f2fd', 
            border: '1px solid #2196f3', 
            borderRadius: 1, 
            p: 2, 
            mb: 2
          }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>
              Your Responsibility
            </Typography>
            <Typography variant="body2" sx={{ color: '#1565c0', lineHeight: 1.6 }}>
              By using Gamura, you acknowledge that you understand the limitations of AI technology and agree to use 
              the service responsibly. You are responsible for validating all information and seeking appropriate professional 
              guidance when needed.
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#666', fontStyle: 'italic', mt: 2 }}>
            For questions or concerns about AI-generated content, please contact our support team or use the feedback 
            feature to report issues.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={onClose} variant="contained" color="primary">
          I Understand
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AILiteracyDisclaimer;

